import React, { useState, useRef } from 'react';
import { X, Image as ImageIcon } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_WIDTH = 1920;
const MAX_HEIGHT = 1080;
const JPEG_QUALITY = 0.85;

export const ImageUpload = ({ onImageUploaded, currentImage }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentImage || '');
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      await uploadFile(files[0]);
    }
  };

  const handleFileSelect = async (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      await uploadFile(files[0]);
    }
  };

  const resizeImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          let width = img.width;
          let height = img.height;
          if (width > MAX_WIDTH || height > MAX_HEIGHT) {
            const ratio = Math.min(MAX_WIDTH / width, MAX_HEIGHT / height);
            width = Math.floor(width * ratio);
            height = Math.floor(height * ratio);
          }
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob(
            (blob) => {
              if (blob) {
                const resizedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                });
                resolve(resizedFile);
              } else {
                reject(new Error('Failed to compress image'));
              }
            },
            'image/jpeg',
            JPEG_QUALITY
          );
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = e.target.result;
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  const uploadFile = async (file) => {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Please upload an image file');
      return;
    }

    setUploading(true);
    
    try {
      alert('Processing image...');
      const processedFile = await resizeImage(file);
      await performUpload(processedFile);
    } catch (error) {
      console.error('Processing error:', error);
      alert('Failed to process image');
      setUploading(false);
    }
  };

  const performUpload = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('admin_token');
      console.log('Uploading to:', `${API}/admin/upload-image`);
      const response = await axios.post(`${API}/admin/upload-image`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      console.log('Upload response:', response.data);
      const imageUrl = `${BACKEND_URL}${response.data.url}`;
      console.log('Constructed image URL:', imageUrl);
      console.log('Testing image accessibility...');
      const testImg = new Image();
      testImg.onload = () => {
        console.log('Image is accessible and loaded successfully');
        setPreview(imageUrl);
        onImageUploaded(imageUrl);
        alert('Image uploaded successfully');
      };
      testImg.onerror = () => {
        console.error('Image URL is not accessible:', imageUrl);
        alert('Image uploaded but cannot be displayed. Check nginx /uploads configuration');
        setPreview(imageUrl);
        onImageUploaded(imageUrl);
      };
      testImg.src = imageUrl;
    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to upload image';
      alert(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const clearImage = () => {
    setPreview('');
    onImageUploaded('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-4">
      {preview ? (
        <div className="relative">
          <img
            src={preview}
            alt="Preview"
            className="w-full h-48 object-cover rounded-lg"
            onError={(e) => {
              console.error('Failed to load image:', preview);
              e.target.style.display = 'none';
              const errorDiv = document.createElement('div');
              errorDiv.className = 'w-full h-48 bg-red-50 border-2 border-red-300 rounded-lg flex items-center justify-center';
              errorDiv.innerHTML = '<p class="text-red-600 text-sm px-4 text-center">Image uploaded but not displaying. URL: ' + preview + '</p>';
              e.target.parentNode.insertBefore(errorDiv, e.target);
            }}
          />
          <Button
            type="button"
            onClick={clearImage}
            className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white p-2"
            size="sm"
          >
            <X size={16} />
          </Button>
        </div>
      ) : (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            isDragging
              ? 'border-red-600 bg-red-50'
              : 'border-gray-300 hover:border-gray-400'
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
            id="image-upload"
          />

          {uploading ? (
            <div className="space-y-2">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
              <p className="text-sm text-gray-600">Uploading...</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex justify-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                  <ImageIcon className="text-gray-400" size={32} />
                </div>
              </div>
              <div>
                <label
                  htmlFor="image-upload"
                  className="cursor-pointer text-red-600 hover:text-red-700 font-medium"
                >
                  Click to upload
                </label>
                <span className="text-gray-600"> or drag and drop</span>
              </div>
              <p className="text-xs text-gray-500">
                PNG, JPG, GIF or WebP (auto-optimized)
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
